<template>
  <div>
    <div
      v-if="isCTAEnable && props.reserveSpace"
      aria-hidden="true"
      class="lg:tw-hidden tw-h-20 tw-shrink-0"
    />

    <Teleport to="body">
      <!-- Main CTA Bar — plain div since QLayout was removed -->
      <div
        v-if="isCTAEnable"
        class="cta-footer fast-mobile-fixed-footer lg:tw-hidden tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-bg-white tw-shadow-lg"
        :class="props.customFooterClass"
      >
        <div class="tw-grid tw-grid-cols-6" :class="props.customPadding">
          <!-- Collapse CTA Button -->
          <slot
            v-if="showCollapseCTA"
            name="collapse"
            :show-bottom-sheet="showBottomSheet"
            custom-class="tw-col-span-2">
            <ButtonPrimary
              class="tw-col-span-2 tw-rounded-lg"
              :class="props.customButtonClass"
              variant="primary"
              outline icon="forum"
              :icon-size="props.customIconSize"
              :icon-class="props.customIconClass"
              @click="showBottomSheet">
              <span :class="props.customButtonTextClass">
                {{ $t('global.contact') }}
              </span>
            </ButtonPrimary>
          </slot>

          <!-- Social Buttons -->
          <template
            v-for="button in bottomBarButtons"
            :key="`button-${button.type}`">
            <slot
              v-if="button.show"
              name="button"
              :button="button"
              :click="() => trackSocialClick(button.label, button.trackUrl, button.eventName)">
              <ButtonExternalLink
                :class="[button.class, props.customButtonClass]"
                :button-class="props.customButtonClass"
                :title-class="props.customButtonTextClass || 'tw-button'"
                :icon-size="props.customIconSize"
                :icon-class="props.customIconClassSocial"
                :title="button.title"
                :link="button.link"
                :variant="button.variant"
                :icon="button.icon" flat
                class="tw-rounded-lg"
                @click="() => trackSocialClick(button.label, button.trackUrl, button.eventName)" />
            </slot>
          </template>
        </div>
      </div>

      <!-- Floating Action Button -->
      <div v-if="isTelEnable || isFacebookEnable || isLineEnable || isWhatsAppEnable"
        class="fast-cta-fab tw-hidden lg:tw-block tw-fixed tw-bottom-[18px] tw-right-[18px] tw-z-50"
        :class="props.fabContainerClass">
        <!-- Single FAB -->
        <QFab v-if="singleContactMethod"
          :href="singleFabHref" :target="singleFabTarget"
          :icon="singleFabIcon" :style="fabPrimaryStyle" direction="up"
          :square="props.fabSquare" :outline="props.fabOutline"
          :class="props.fabClass" @click="handleSingleFabClick">
          <template #tooltip>
            <span class="tw-sr-only">{{ $t('global.contact') }}</span>
          </template>
        </QFab>

        <!-- Multiple FAB -->
        <QFab v-else icon="forum" active-icon="close" :style="fabPrimaryStyle" direction="up"
          :square="props.fabSquare" :outline="props.fabOutline"
          :class="props.fabClass">
          <template #tooltip>
            <span class="tw-sr-only">{{ $t('global.contact') }}</span>
          </template>
          <QFabAction v-for="action in fabActions" :key="`fab-action-${action.platform}`"
            :color="props.fabActionColor || action.color" :icon="action.icon"
            :square="props.fabSquare" :outline="props.fabOutline"
            :class="props.fabClass"
            :href="action.link" :target="action.target"
            @click.prevent="action.onClick" />
        </QFab>
      </div>
    </Teleport>
  </div>
</template>


<script setup lang="ts">
import throttle from 'lodash.throttle'
import {
  SOCIAL_PLATFORM,
  SOCIAL_LABEL,
  SOCIAL_ICONS,
  ANALYTICS_EVENTS,
  ANALYTICS_CATEGORY,
} from '~/constants/social'

const props = defineProps({
  hasBottomSheetBackground: {
    type: Boolean,
    default: false,
  },
  bottomSheetClass: {
    type: String,
    default: '',
  },
  bottomSheetActionClass: {
    type: String,
    default: '',
  },
  fabOutline: {
    type: Boolean,
    default: false,
  },
  fabSquare: {
    type: Boolean,
    default: false,
  },
  fabClass: {
    type: String,
    default: '',
  },
  fabContainerClass: {
    type: String,
    default: '',
  },
  fabActionColor: {
    type: String,
    default: '',
  },
  customFacebookIcon: {
    type: String,
    default: '',
  },
  useChatLabel: {
    type: Boolean,
    default: false,
  },
  hideBottomSheetTel: {
    type: Boolean,
    default: false,
  },
  customPadding: {
    type: String,
    default: 'tw-gap-4 tw-p-4',
  },
  customFooterClass: {
    type: String,
    default: '',
  },
  reserveSpace: {
    type: Boolean,
    default: true,
  },
  customButtonClass: {
    type: String,
    default: '',
  },
  customButtonTextClass: {
    type: String,
    default: 'tw-text-dt-body-small',
  },
  customIconSize: {
    type: String,
    default: '18px',
  },
  customIconClass: {
    type: String,
    default: 'tw-mr-2',
  },
  customIconClassSocial: {
    type: String,
    default: 'tw-mr-2',
  },
  showBottomSheetClose: {
    type: Boolean,
    default: false,
  },
})

// Refs
const showCallToAction = ref(false)

// Computed
const contactStore = useContactStore()
const { getWhatsAppLink, getMessengerLink, getLineLink } = useSocialLink()
const phoneNumber = computed(() => contactStore.phoneNumbers[0])
const whatsAppLink = computed(() => getWhatsAppLink(contactStore.whatsappId))
const messengerLink = computed(() => getMessengerLink(contactStore.facebookId))
const lineLink = computed(() => getLineLink(contactStore.lineId))
const telLink = computed(() => `tel:${phoneNumber.value}`)

const isWhatsAppEnable = computed(() => !!(contactStore.enableButton.whatsapp && contactStore.whatsappId))
const isFacebookEnable = computed(() => !!(contactStore.enableButton.facebook && contactStore.facebookId))
const isLineEnable = computed(() => !!(contactStore.enableButton.line && contactStore.lineId))
const isTelEnable = computed(() => !!(contactStore.enableButton.tel && contactStore.phoneNumbers.length > 0))
const isCTAEnable = computed(() => isWhatsAppEnable.value || isFacebookEnable.value || isLineEnable.value || isTelEnable.value)

const socialCount = computed(() => (+isWhatsAppEnable.value) + (+isFacebookEnable.value) + (+isLineEnable.value))

const showCollapseCTA = computed(() =>
  (isTelEnable.value && socialCount.value > 0) || (socialCount.value > 2),
)

// Define type for button variants to match ButtonExternalLink component
type ButtonVariant = 'whatsapp' | 'facebook' | 'line' | 'primary'

// Bottom bar button configurations
const bottomBarButtons = computed(() => [
  {
    type: 'whatsapp',
    show: (isWhatsAppEnable.value && !isTelEnable.value && socialCount.value > 1) ||
      (isWhatsAppEnable.value && !isTelEnable.value),
    class: {
      'tw-col-span-6': socialCount.value === 1,
      'tw-col-span-3': socialCount.value === 2,
      'tw-col-span-4': socialCount.value > 2,
    },
    title: (socialCount.value === 1 && !isTelEnable.value)
      ? t('global.chatBySocial', { title: 'WhatsApp' })
      : t('global.chat'),
    link: whatsAppLink.value,
    variant: 'whatsapp' as ButtonVariant,
    icon: 'fa-brands fa-whatsapp',
    label: SOCIAL_LABEL.WHATSAPP,
    trackUrl: whatsAppLink.value,
    eventName: ANALYTICS_EVENTS.CLICK_CTA_WHATSAPP,
  },
  {
    type: 'facebook',
    show: !isTelEnable.value && socialCount.value <= 2 && isFacebookEnable.value,
    class: {
      'tw-col-span-6': socialCount.value === 1,
      'tw-col-span-3': socialCount.value === 2,
      'tw-col-span-2': socialCount.value > 2,
    },
    title: (socialCount.value === 1 && !isTelEnable.value)
      ? t('global.chatBySocial', { title: 'Facebook' })
      : t('global.chat'),
    link: messengerLink.value,
    variant: 'facebook' as ButtonVariant,
    icon: props.customFacebookIcon || 'fa-brands fa-facebook',
    label: SOCIAL_LABEL.FACEBOOK,
    trackUrl: messengerLink.value,
    eventName: ANALYTICS_EVENTS.CLICK_CTA_FACEBOOK,
  },
  {
    type: 'line',
    show: !isTelEnable.value && socialCount.value <= 2 && isLineEnable.value,
    class: {
      'tw-col-span-6': socialCount.value === 1,
      'tw-col-span-3': socialCount.value === 2,
    },
    title: (socialCount.value === 1 && !isTelEnable.value)
      ? t('global.chatBySocial', { title: 'Line' })
      : t('global.chat'),
    link: lineLink.value,
    variant: 'line' as ButtonVariant,
    icon: 'fa-brands fa-line',
    label: SOCIAL_LABEL.LINE,
    trackUrl: lineLink.value,
    eventName: ANALYTICS_EVENTS.CLICK_CTA_LINE,
  },
  {
    type: 'tel',
    show: isTelEnable.value,
    class: {
      'tw-col-span-6': socialCount.value === 0,
      'tw-col-span-4': socialCount.value > 0,
    },
    title: socialCount.value === 0
      ? t('global.contactByTel')
      : t('global.callNow'),
    link: telLink.value,
    variant: 'primary' as ButtonVariant,
    icon: 'phone',
    label: SOCIAL_LABEL.TEL,
    trackUrl: phoneNumber.value,
    eventName: ANALYTICS_EVENTS.CLICK_CTA_TEL,
  },
])

// Single FAB computeds
const singleContactMethod = computed(() => {
  const enabledMethods = [isTelEnable.value, isLineEnable.value, isFacebookEnable.value, isWhatsAppEnable.value]
  return enabledMethods.filter(Boolean).length === 1
})

const singleFabIcon = computed(() => {
  if (isTelEnable.value) { return SOCIAL_ICONS.TEL }
  if (isLineEnable.value) { return SOCIAL_ICONS.LINE }
  if (isFacebookEnable.value) { return SOCIAL_ICONS.FACEBOOK }
  if (isWhatsAppEnable.value) { return SOCIAL_ICONS.WHATSAPP }
  return 'forum'
})

const fabPrimaryStyle = computed(() => {
  return {
    backgroundColor: 'var(--theme-color-primary)',
    color: 'var(--theme-color-inverted-primary)',
  }
})

const singleFabHref = computed(() => {
  if (isTelEnable.value) return telLink.value
  if (isLineEnable.value) return lineLink.value
  if (isFacebookEnable.value) return messengerLink.value
  if (isWhatsAppEnable.value) return whatsAppLink.value
  return '#'
})

const singleFabTarget = computed(() => isTelEnable.value ? '_self' : '_blank')

// Define interfaces for FAB and Contact actions to ensure proper typing
interface FabAction {
  platform: string
  color: 'primary' | 'line' | 'facebook' | 'whatsapp' // Specific color values for Quasar compatibility
  icon: string
  link: string
  target: '_blank' | '_self'
  onClick: () => void
}

interface ContactAction {
  label: string
  icon: string
  onClick: () => void
  classes: string
}

// Note: We use the color values from SOCIAL_COLORS constants that match Quasar's expected color types

/**
 * Provides structured data for FAB action buttons
 * Each item has platform, color, icon and click handler
 */
const fabActions = computed(() => {
  const actions: FabAction[] = []

  if (isTelEnable.value) {
    actions.push({
      platform: SOCIAL_PLATFORM.TEL,
      color: 'primary',
      icon: SOCIAL_ICONS.TEL,
      link: telLink.value,
      target: '_self',
      onClick: () => handleFabActionClick(SOCIAL_PLATFORM.TEL, telLink.value, '_self', SOCIAL_LABEL.TEL, phoneNumber.value),
    })
  }

  if (isLineEnable.value) {
    actions.push({
      platform: SOCIAL_PLATFORM.LINE,
      color: 'line',
      icon: SOCIAL_ICONS.LINE,
      link: lineLink.value,
      target: '_blank',
      onClick: () => handleFabActionClick(SOCIAL_PLATFORM.LINE, lineLink.value, '_blank', SOCIAL_LABEL.LINE, lineLink.value),
    })
  }

  if (isFacebookEnable.value) {
    actions.push({
      platform: SOCIAL_PLATFORM.FACEBOOK,
      color: 'facebook',
      icon: props.customFacebookIcon || SOCIAL_ICONS.FACEBOOK,
      link: messengerLink.value,
      target: '_blank',
      onClick: () => handleFabActionClick(SOCIAL_PLATFORM.FACEBOOK, messengerLink.value, '_blank', SOCIAL_LABEL.FACEBOOK, messengerLink.value),
    })
  }

  if (isWhatsAppEnable.value) {
    actions.push({
      platform: SOCIAL_PLATFORM.WHATSAPP,
      color: 'whatsapp',
      icon: SOCIAL_ICONS.WHATSAPP,
      link: whatsAppLink.value,
      target: '_blank',
      onClick: () => handleFabActionClick(SOCIAL_PLATFORM.WHATSAPP, whatsAppLink.value, '_blank', SOCIAL_LABEL.WHATSAPP, whatsAppLink.value),
    })
  }

  return actions
})

const { t } = useI18n()
// Contact actions list
const contactActions = computed(() => {
  const actions: ContactAction[] = []

  if (isWhatsAppEnable.value) {
    actions.push({
      label: props.useChatLabel ? t('global.chatBySocial', { title: 'WhatsApp' }) : t('global.contactByWhatsApp'),
      icon: SOCIAL_ICONS.WHATSAPP,
      onClick: () => handleFabClick(SOCIAL_PLATFORM.WHATSAPP, whatsAppLink.value, SOCIAL_LABEL.WHATSAPP, whatsAppLink.value),
      classes: `${props.hasBottomSheetBackground ? 'bg-whatsapp text-white' : 'text-whatsapp tw-border tw-border-whatsapp tw-rounded-lg tw-m-2'} ${props.bottomSheetActionClass}`,
    })
  }

  if (isFacebookEnable.value) {
    actions.push({
      label: props.useChatLabel ? t('global.chatBySocial', { title: 'Facebook' }) : t('global.contactByFacebook'),
      icon: props.customFacebookIcon || SOCIAL_ICONS.FACEBOOK,
      onClick: () => handleFabClick(SOCIAL_PLATFORM.FACEBOOK, messengerLink.value, SOCIAL_LABEL.FACEBOOK, messengerLink.value),
      classes: `${props.hasBottomSheetBackground ? 'bg-facebook text-white' : 'text-facebook tw-border tw-border-facebook tw-rounded-lg tw-m-2'} ${props.bottomSheetActionClass}`,
    })
  }

  if (isLineEnable.value) {
    actions.push({
      label: props.useChatLabel ? t('global.chatBySocial', { title: 'Line' }) : t('global.contactByLine'),
      icon: SOCIAL_ICONS.LINE,
      onClick: () => handleFabClick(SOCIAL_PLATFORM.LINE, lineLink.value, SOCIAL_LABEL.LINE, lineLink.value),
      classes: `${props.hasBottomSheetBackground ? 'bg-line text-white' : 'text-line tw-border tw-border-line tw-rounded-lg tw-m-2'} ${props.bottomSheetActionClass}`,
    })
  }

  if (isTelEnable.value && !props.hideBottomSheetTel) {
    actions.push({
      label: props.useChatLabel ? t('global.chatByTel') : t('global.contactByTel'),
      icon: SOCIAL_ICONS.TEL,
      onClick: () => handleFabClick(SOCIAL_PLATFORM.TEL, telLink.value, SOCIAL_LABEL.TEL, phoneNumber.value),
      classes: `${props.hasBottomSheetBackground ? 'bg-primary text-white' : 'text-primary tw-border tw-border-primary tw-rounded-lg tw-m-2'} ${props.bottomSheetActionClass}`,
    })
  }

  return actions
})

// Methods
const handleCallToAction = throttle((e: Event) => {
  const target = e.target as Document
  if (target?.scrollingElement) {
    showCallToAction.value = target.scrollingElement.scrollTop >= 300
  }
}, 500)

const $q = useQuasar()
const showBottomSheet = () => {
  const dialog = $q.bottomSheet({
    message: `${t('global.contact')}`,
    actions: contactActions.value,
    class: props.bottomSheetClass,
  }).onOk((action) => {
    action.onClick()
  })

  if (props.showBottomSheetClose && props.bottomSheetClass) {
    nextTick(() => {
      requestAnimationFrame(() => {
        const sheet = document.querySelector(`.${props.bottomSheetClass}`)
        const messageEl = sheet?.querySelector('.q-dialog__message') as HTMLElement
        if (messageEl && !messageEl.querySelector('.cta-close-btn')) {
          messageEl.style.display = 'flex'
          messageEl.style.justifyContent = 'space-between'
          messageEl.style.alignItems = 'center'
          const closeBtn = document.createElement('button')
          closeBtn.className = 'cta-close-btn'
          closeBtn.type = 'button'
          closeBtn.innerHTML = '&#10005;'
          closeBtn.style.cssText = 'background:none;border:none;cursor:pointer;font-size:18px;color:#000;padding:0;line-height:1;'
          closeBtn.addEventListener('click', () => dialog.hide())
          messageEl.appendChild(closeBtn)
        }
      })
    })
  }
}

const handleSingleFabClick = () => {
  // href handles navigation; only track the click here
  if (isTelEnable.value) {
    trackSocialClick(SOCIAL_LABEL.TEL, phoneNumber.value, `click_cta_${SOCIAL_PLATFORM.TEL}`)
  } else if (isLineEnable.value) {
    trackSocialClick(SOCIAL_LABEL.LINE, lineLink.value, `click_cta_${SOCIAL_PLATFORM.LINE}`)
  } else if (isFacebookEnable.value) {
    trackSocialClick(SOCIAL_LABEL.FACEBOOK, messengerLink.value, `click_cta_${SOCIAL_PLATFORM.FACEBOOK}`)
  } else if (isWhatsAppEnable.value) {
    trackSocialClick(SOCIAL_LABEL.WHATSAPP, whatsAppLink.value, `click_cta_${SOCIAL_PLATFORM.WHATSAPP}`)
  }
}

const handleFabClick = (type: string, link: string, platform: string, trackUrl: string) => {
  trackSocialClick(platform, trackUrl, `click_cta_${type}`)
  window.open(link, '_blank')
}

const handleFabActionClick = (type: string, link: string, target: '_blank' | '_self', platform: string, trackUrl: string) => {
  trackSocialClick(platform, trackUrl, `click_cta_${type}`)

  if (target === '_self') {
    window.location.href = link
    return
  }

  window.open(link, target, 'noopener')
}

const { analyticEvent, fbPixelEvent } = useAnalytic()
const trackSocialClick = (platform: string, url: string, eventName: string) => {
  analyticEvent({
    ga4ActionName: eventName,
    ga4Label: url,
  })
  fbPixelEvent({
    fbCategory: ANALYTICS_CATEGORY.CONTACT,
    fbEvent: eventName,
    fbValue: { platform, url },
  })
}
// Lifecycle hooks
onMounted(() => {
  window.addEventListener('scroll', handleCallToAction)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleCallToAction)
})
</script>

<style>
.fast-mobile-fixed-footer {
  will-change: transform;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  contain: paint;
}

.fast-cta-fab {
  z-index: 6001 !important;
}

.fast-cta-fab .q-fab__actions--opened,
.fast-cta-fab .q-fab__actions--opened .q-btn {
  pointer-events: auto;
}

.fast-cta-fab .q-btn--fab,
.fast-cta-fab .q-btn--fab::before,
.fast-cta-fab .q-btn--fab .q-focus-helper,
.fast-cta-fab .q-btn--fab .q-btn__content,
.fast-cta-fab .q-btn--fab-mini,
.fast-cta-fab .q-btn--fab-mini::before,
.fast-cta-fab .q-btn--fab-mini .q-focus-helper,
.fast-cta-fab .q-btn--fab-mini .q-btn__content,
.fast-cta-fab .q-btn--round,
.fast-cta-fab .q-btn--round::before,
.fast-cta-fab .q-btn--round .q-focus-helper,
.fast-cta-fab .q-btn--round .q-btn__content {
  border-radius: 9999px !important;
}

.q-fab--opened > .q-btn--fab {
  background-color: var(--theme-color-primary) !important;
}
</style>
